import { type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';

interface AccessDeniedProps {
  feature?: string | null;
  message?: string | null;
}

function AccessDenied({ feature = null, message = null }: AccessDeniedProps) {
  const navigate = useNavigate();

  return (
    <div style={adStyles.container}>
      <div style={adStyles.card}>
        <div style={adStyles.icon}>🔒</div>
        <h2 style={adStyles.title}>Access Denied</h2>
        <p style={adStyles.message}>
          {message || "You don't have permission to access this page."}
        </p>
        {feature && (
          <p style={adStyles.featureHint}>
            Required permission: <code style={adStyles.code}>{feature}</code>
          </p>
        )}
        <div style={adStyles.actions}>
          <button
            onClick={() => navigate(-1)}
            style={adStyles.backButton}
          >
            Go Back
          </button>
          <button
            onClick={() => navigate('/')}
            style={adStyles.homeButton}
          >
            Go to Home
          </button>
        </div>
        <p style={adStyles.helpText}>
          If you believe you should have access, please contact your administrator.
        </p>
      </div>
    </div>
  );
}

const adStyles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    padding: '2rem',
  },
  card: {
    background: 'white',
    borderRadius: 'var(--radius-lg)',
    padding: '3rem',
    textAlign: 'center',
    maxWidth: '400px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  },
  icon: {
    fontSize: '4rem',
    marginBottom: '1rem',
  },
  title: {
    margin: '0 0 0.75rem 0',
    fontSize: 'var(--text-4xl)',
    fontWeight: 600,
    color: '#1f2937',
  },
  message: {
    margin: '0 0 1rem 0',
    fontSize: 'var(--text-xl)',
    color: 'var(--color-text-light)',
    lineHeight: 1.5,
  },
  featureHint: {
    margin: '0 0 1.5rem 0',
    fontSize: 'var(--text-base)',
    color: 'var(--color-text-light)',
  },
  code: {
    background: 'var(--color-bg)',
    padding: '0.125rem 0.5rem',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--text-md)',
    fontFamily: 'monospace',
  },
  actions: {
    display: 'flex',
    gap: '0.75rem',
    justifyContent: 'center',
    marginBottom: '1.5rem',
  },
  backButton: {
    padding: '0.625rem 1.25rem',
    fontSize: 'var(--text-base)',
    fontWeight: 500,
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 'var(--radius)',
    cursor: 'pointer',
    color: 'var(--color-sidebar-hover)',
    transition: 'all 0.2s',
  },
  homeButton: {
    padding: '0.625rem 1.25rem',
    fontSize: 'var(--text-base)',
    fontWeight: 500,
    background: 'var(--color-primary, #772cbb)',
    border: 'none',
    borderRadius: 'var(--radius)',
    cursor: 'pointer',
    color: 'white',
    transition: 'all 0.2s',
  },
  helpText: {
    margin: 0,
    fontSize: 'var(--text-md)',
    color: 'var(--color-text-light)',
  },
};

export default AccessDenied;
